import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Add, Delete, Edit, Lock, LockOpen, Source, Storage, Visibility} from "@mui/icons-material";
import {Button, DialogActions, DialogContent, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getPackageInternalUrl} from "../../constants/appRoutes";
import {NODE_TYPE_MERGE} from "../../constants/nodes";
import Call from "../../hocs/call";
import AlertDialog from "../alert-dialog";
import ConfirmationDialog from "../confirmation-dialog";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import {PACKAGE_MANAGER_SOURCE_USER_ITEMS} from "./constants";
import PackageSettings from "./PackageSettings";
import ResourceList from "./ResourceList";
import {appSelector} from "../../state/app/appSelectors";
import {
  approvePackage,
  deletePackage,
  disapprovePackage,
  fetchPackageList,
  fetchPackageOwnership,
  hidePackageList,
  PACKAGE_MANAGER_PACKAGE_LIST_SPINNER,
  setPackageOwnership,
  showPackage,
  showResourceList
} from "../../state/package-manager/packageManagerActions";
import {packageManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {addSpinnerMessage, markSpinnerMessage} from "../../state/spinner/spinnerActions";
import {userSelector} from "../../state/user/userSelectors";
import {localizeI18nObj} from "../../utils/i18n";
import {getIsUserSuperadmin} from "../../utils/permissions";

const PackageList = ({node, onNavigate, source}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(userSelector);
  const {language, languages} = useSelector(appSelector);
  const {readOnly, isPackageListModalVisible, nodeId, nodeCode, packages, packagesOwnerMap} =
    useSelector(packageManagerSelector);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isPackageDeleteBlockedAlertOpen, setIsPackageDeleteBlockedAlertOpen] = useState(false);

  const visiblePackages = useMemo(
    () =>
      source === PACKAGE_MANAGER_SOURCE_USER_ITEMS
        ? (packages || []).filter(pkg => pkg.owner === user?.userId)
        : packages || [],
    [packages, source, user?.userId]
  );

  const fetchOwnership = nodeId => {
    dispatch(
      addSpinnerMessage(
        PACKAGE_MANAGER_PACKAGE_LIST_SPINNER,
        t("modules.packageManager.actions.messages.getPackageList")
      )
    );
    if (readOnly) {
      dispatch(setPackageOwnership());
    } else {
      dispatch(fetchPackageOwnership(nodeId));
    }
  };

  const fetchPackages = nodeId => {
    dispatch(fetchPackageList(nodeId));
  };

  useEffect(() => {
    if (packages) dispatch(markSpinnerMessage(PACKAGE_MANAGER_PACKAGE_LIST_SPINNER));
  }, [packages, dispatch]);

  const confirmConfig = useMemo(
    () => ({
      approve: {
        title: t("modules.packageManager.packageList.approveDialog.title"),
        content: t("modules.packageManager.packageList.approveDialog.description"),
        action: approvePackage
      },
      disapprove: {
        title: t("modules.packageManager.packageList.disapproveDialog.title"),
        content: t("modules.packageManager.packageList.disapproveDialog.description"),
        action: disapprovePackage
      },
      delete: {
        title: t("modules.packageManager.packageList.deleteDialog.title"),
        content: t("modules.packageManager.packageList.deleteDialog.description"),
        action: deletePackage
      }
    }),
    [t]
  );

  const handleConfirm = useCallback(() => {
    if (!selectedPackage) return;

    const config = confirmConfig[selectedPackage.action];
    if (!config) return;

    dispatch(config.action(nodeId, selectedPackage.id));
    setSelectedPackage(null);
  }, [dispatch, confirmConfig, nodeId, selectedPackage]);

  const canManagePackageApproval = Boolean(node) && node.type !== NODE_TYPE_MERGE;

  return (
    <SettingsDialog
      title={t("modules.packageManager.dialog.title")}
      titleProps={{
        icon: <Source />
      }}
      open={isPackageListModalVisible}
      onClose={() => dispatch(hidePackageList())}
    >
      <Call cb={fetchOwnership} cbParam={nodeId} disabled={!nodeId || Boolean(packagesOwnerMap)}>
        <Call cb={fetchPackages} cbParam={nodeId} disabled={!nodeId || !packagesOwnerMap || Boolean(packages)}>
          {packages && (
            <MaterialTable
              data={visiblePackages}
              rightActions={
                !readOnly
                  ? [
                      {
                        id: "package-list__right-actions__create-package-dropdown",
                        label: t("modules.packageManager.packageList.createPackage.label"),
                        startIcon: <Add />,
                        onClick: () => dispatch(showPackage())
                      }
                    ]
                  : []
              }
              columns={[
                {
                  hidden: true,
                  field: "id",
                  title: t("modules.packageManager.packageList.columns.id.label"),
                  getClassName: () => "package-list__columns__id"
                },
                {
                  field: "title",
                  title: t("modules.packageManager.packageList.columns.title.label"),
                  render: ({title}) => localizeI18nObj(title, language, languages),
                  getClassName: () => "package-list__columns__title"
                }
              ]}
              actions={[
                dataset =>
                  canManagePackageApproval
                    ? dataset.public
                      ? {
                          className: "package-list__actions__approve-dataset",
                          icon: <LockOpen />,
                          label: t("modules.packageManager.packageList.approveDataset.disapprove.label"),
                          tooltip: t("modules.packageManager.packageList.approveDataset.disapprove.label"),
                          onClick: (_, {id}) => setSelectedPackage({id: id, action: "disapprove"})
                        }
                      : {
                          className: "package-list__actions__approve-dataset",
                          icon: <Lock />,
                          label: t("modules.packageManager.packageList.approveDataset.approve.label"),
                          tooltip: t("modules.packageManager.packageList.approveDataset.approve.label"),
                          onClick: (_, {id}) => setSelectedPackage({id: id, action: "approve"})
                        }
                    : null,
                {
                  className: "package-list__actions__link-dataset",
                  icon: <Visibility />,
                  tooltip: t("modules.packageManager.packageList.datasetLink.label"),
                  onClick: (_, {id}) => {
                    if (onNavigate) onNavigate();
                    dispatch(hidePackageList());
                    navigate(getPackageInternalUrl(nodeCode, [], id, null));
                  }
                },
                {
                  className: "package-list__actions__show-resources",
                  icon: <Storage />,
                  tooltip: t("modules.packageManager.packageList.resourceList.label"),
                  onClick: (_, {id}) => dispatch(showResourceList(id))
                },
                dataset =>
                  readOnly
                    ? null
                    : {
                        className: "package-list__actions__edit-package",
                        icon: <Edit />,
                        tooltip: t("modules.packageManager.packageList.editPackage.label"),
                        onClick: (_, {id}) => dispatch(showPackage(id)),
                        disabled: !getIsUserSuperadmin(user) && dataset.owner !== user.userId
                      },
                dataset =>
                  readOnly
                    ? null
                    : {
                        className: "package-list__actions__delete-package",
                        icon: <Delete />,
                        tooltip: t("modules.packageManager.packageList.deletePackage.label"),
                        onClick: (_, {id, resources}) =>
                          (resources || []).length
                            ? setIsPackageDeleteBlockedAlertOpen(true)
                            : setSelectedPackage({id: id, action: "delete"}),
                        disabled: !getIsUserSuperadmin(user) && dataset.owner !== user.userId
                      }
              ]}
            />
          )}
        </Call>
      </Call>

      <PackageSettings />

      <ResourceList node={node} />

      <ConfirmationDialog
        title={confirmConfig[selectedPackage?.action]?.title}
        content={confirmConfig[selectedPackage?.action]?.content}
        open={Boolean(selectedPackage)}
        onClose={() => setSelectedPackage(null)}
        onConfirm={handleConfirm}
      />

      <AlertDialog
        title={t("modules.packageManager.packageList.dialog.packageDeleteBlocked.title")}
        open={isPackageDeleteBlockedAlertOpen}
        onClose={() => setIsPackageDeleteBlockedAlertOpen(false)}
      >
        <DialogContent className="package-list__package-delete-blocked package-list__package-delete-blocked--delete">
          <Typography>{t("modules.packageManager.packageList.dialog.packageDeleteBlocked.content")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPackageDeleteBlockedAlertOpen(false)}>{t("commons.confirm.close")}</Button>
        </DialogActions>
      </AlertDialog>
    </SettingsDialog>
  );
};

export default PackageList;
